/// <amd-module name="Comments/Reactions/Components/CommentReactionSelector" />
import { ICommentReactionSelectorProps } from "Comments/Reactions/Components/CommentReactionSelector.Props";
import { ReactionTypeToEmojiMap } from "Comments/Reactions/Constants";
import * as ReactionResources from "Comments/Reactions/Resources";
import * as React from "react";
import { css, VssComponent } from "VSS/Platform/Layout";
import { Button, ExpandableButton } from "VSSUI/Button";
import { Callout } from "VSSUI/Callout";
import { IExpandable } from "VSSUI/Expandable";
import { FocusZoneDirection, FocusZoneKeyStroke } from "VSSUI/FocusZone";
import { KeyCode, isSafari } from "VSSUI/Util";
import { IOffset, IOrigin, IPoint } from "VSSUI/Utilities/Position";

// Component that renders the 'Reaction' button and handles all of the supported reaction types and their interactions
export class CommentReactionSelector extends React.PureComponent<ICommentReactionSelectorProps> {
    public render(): JSX.Element {
        const {
            readonly,
            className,
            onAddReactionButtonClick,
            onSelectionDismiss,
            onFocus,
            onBlur,
            excludeTabStop,
            ariaPosInSet,
            ariaSetSize
        } = this.props;
        if (readonly) {
            return <></>;
        }

        return (
            <ExpandableButton
                className={css("comment-reaction-button", className)}
                iconProps={{ iconName: "AddReaction" }}
                tooltipProps={{ text: ReactionResources.AddYourReaction }}
                onExpand={onAddReactionButtonClick}
                onCollapse={onSelectionDismiss}
                onFocus={onFocus}
                onBlur={onBlur}
                renderCallout={this._onRenderReactionDropdown}
                excludeTabStop={excludeTabStop}
                role="menuitem"
                ariaPosInSet={ariaPosInSet}
                ariaSetSize={ariaSetSize}
                ariaLabel={ReactionResources.AddYourReaction}
                hideDropdownIcon
                subtle
            />
        );
    }

    private _onRenderReactionDropdown = (
        dropdown: IExpandable,
        dropdownId: string,
        anchorElement?: HTMLElement,
        anchorOffset?: IOffset,
        anchorOrigin?: IOrigin,
        anchorPoint?: IPoint,
        dropdownOrigin?: IOrigin
    ): JSX.Element => {
        const { commentId, onAddReaction, onDeleteReaction, reactionTypeToInfoMap } = this.props;

        const items: JSX.Element[] = Object.keys(ReactionTypeToEmojiMap).map((reactionType, index, array) => {
            const reactionTypeNum = Number(reactionType);
            const reactionTypeEmojiInfo = ReactionTypeToEmojiMap[reactionTypeNum];
            const reactionTypeInfo = reactionTypeToInfoMap[reactionTypeNum];

            const onReactionClick = (): void => {
                dropdown.collapse();

                if (reactionTypeInfo && reactionTypeInfo.isCurrentUserEngaged) {
                    onDeleteReaction(commentId, reactionTypeNum);
                } else {
                    onAddReaction(commentId, reactionTypeNum);
                }
            };

            // Prevent onClick events from being overriden by earlier events in Safari
            const onMouseDown = (event: React.MouseEvent<HTMLElement>): void => {
                event.preventDefault();
            }

            return (
                <Button
                    key={reactionType}
                    className="reaction-button-item"
                    onClick={onReactionClick}
                    onMouseDown={isSafari() ? onMouseDown : undefined}
                    tooltipProps={{ text: reactionTypeEmojiInfo.description }}
                    excludeTabStop={index > 0}
                    role="menuitem"
                    ariaPosInSet={index + 1}
                    ariaSetSize={array.length}
                    ariaLabel={reactionTypeEmojiInfo.description}
                >
                    <span dangerouslySetInnerHTML={{ __html: reactionTypeEmojiInfo.encodedValue }} />
                </Button>
            );
        });

        const onDismissCallout = (): void => {
            dropdown.collapse();
        };

        if (items.length > 0) {
            return (
                <Callout
                    anchorElement={anchorElement}
                    anchorOffset={anchorOffset}
                    anchorOrigin={anchorOrigin}
                    anchorPoint={anchorPoint}
                    calloutOrigin={dropdownOrigin}
                    onDismiss={onDismissCallout}
                    className="reaction-selection-container"
                    role="menu"
                    focuszoneProps={{
                        direction: FocusZoneDirection.Horizontal,
                        focusOnMount: true,
                        circularNavigation: true,
                        preprocessKeyStroke: this._preprocessKeyStroke
                    }}
                    blurDismiss
                    contentShadow
                    escDismiss
                >
                    {items}
                </Callout>
            );
        } else {
            return <></>;
        }
    };

    private _preprocessKeyStroke = (event: React.KeyboardEvent<HTMLDivElement>): FocusZoneKeyStroke => {
        if (!event.defaultPrevented && event.which === KeyCode.tab) {
            event.preventDefault();
        }

        return FocusZoneKeyStroke.IgnoreNone;
    };
}

VssComponent.register("comment-reaction-selector", CommentReactionSelector);
